import { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import { NavBar } from "../appBars/navBar/navBar";
import StoreIcon from "../../logo/store.png"
import HomeIcon from "../../logo/home.png"
import ClosetIcon from "../../logo/closet.png"
import BusinessLockerIcon from "../../logo/businessLocker.png"
import { WhereToItem } from "./whereToItem"
import { DeliveryClient, ServiceClient, SettingClient } from "../../helpers/client";
import { DeliveryOptions, DeliveryType, ServiceItem } from "orderme-api-integration-client";
import { ImageLoader } from "../imgLoader/imgLoader";
import { useTranslation } from 'react-i18next';
import { BreadcrumbsBar } from "../breadcrumbs/breadcrumbsBar";
import { clearDelivery, updateDeliveryService, updateFreeDeliveryFrom } from "../../redux/reducers/pickupReducer";
import { useAppDispatch } from "../../redux/hooks";
import { appStyleMode } from "../../helpers/clientConfigs";
import { AppStyle } from "../../helpers/appStyle";
import { useRouteNavigator } from "../../routes/useRouteNavigator";
import { CartRoute, WhereToRoute } from "../../routes/allRoutes";
import { onlyPhysicalItemsInBasket } from "../../helpers/cartVerifier";
import { cartWorth } from "../../helpers/costCounter";

interface DeliveryDetails {
  serviceId: string;
  freeDeliveryFrom: number;
  service: ServiceItem | undefined;
  price: number;
}

export function WhereTo() {
  const routeNavigator = useRouteNavigator();
  const dispatch = useAppDispatch();
  const deliveryClient: DeliveryClient = new DeliveryClient();
  const settingClient: SettingClient = new SettingClient();
  const serviceClient: ServiceClient = new ServiceClient();
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOptions[]>();

  const initialDeliveryState: DeliveryDetails = {
    serviceId: "",
    freeDeliveryFrom: 0,
    service: undefined,
    price: 0,
  };

  const [homeDelivery, setHomeDelivery] = useState<DeliveryDetails>(initialDeliveryState);
  const [parcelLockerDelivery, setParcelLockerDelivery] = useState<DeliveryDetails>(initialDeliveryState);
  const [businessLockerDelivery, setBusinessLockerDelivery] = useState<DeliveryDetails>(initialDeliveryState);

  const { t } = useTranslation();
  const priceUpToFreeDelivery = homeDelivery.freeDeliveryFrom > 0 ? homeDelivery.freeDeliveryFrom - cartWorth() : 0;
  const parcelLockerPriceUpToFreeDelivery = parcelLockerDelivery.freeDeliveryFrom > 0 ?
    parcelLockerDelivery.freeDeliveryFrom - cartWorth() : 0;
  const businessLockerPriceUpToFreeDelivery = businessLockerDelivery.freeDeliveryFrom > 0 ?
    businessLockerDelivery.freeDeliveryFrom - cartWorth() : 0;

  useEffect(() => {
    getDeliverySettings();
    getDeliveryOptions();
    dispatch(clearDelivery());
  }, []);

  useEffect(() => {
    if (!homeDelivery.serviceId)
      return;

    serviceClient.getServiceById(homeDelivery.serviceId).then((response) => {
      setHomeDelivery({
        ...homeDelivery,
        service: response,
        price: response.price,
      });
    }).catch((err) => console.log(err));
  }, [homeDelivery.serviceId]);

  useEffect(() => {
    if (!parcelLockerDelivery.serviceId)
      return;

    serviceClient.getServiceById(parcelLockerDelivery.serviceId).then((response) => {
      setParcelLockerDelivery({
        ...parcelLockerDelivery,
        service: response,
        price: response.price,
      });
    }).catch((err) => console.log(err));
  }, [parcelLockerDelivery.serviceId]);

  useEffect(() => {
    if (!businessLockerDelivery.serviceId)
      return;

    serviceClient.getServiceById(businessLockerDelivery.serviceId).then((response) => {
      setBusinessLockerDelivery({
        ...businessLockerDelivery,
        service: response,
        price: response.price,
      });
    }).catch((err) => console.log(err));
  }, [businessLockerDelivery.serviceId]);

  const getDeliveryOptions = () => {
    deliveryClient.get().then((response) => {
      setDeliveryOptions(response);
      console.log(response);
    }).catch((err) => console.log(err));
  }

  const getDeliverySettings = () => {
    settingClient.get().then((response) => {
      setHomeDelivery({
        ...homeDelivery,
        serviceId: response.orders.deliveryServiceId,
        freeDeliveryFrom: response.orders.freeDeliverFrom
      });
      setParcelLockerDelivery({
        ...parcelLockerDelivery,
        serviceId: response.orders.parcelLockerDeliveryServiceId,
        freeDeliveryFrom: response.orders.parcelLockerFreeDeliverFrom
      });
      setBusinessLockerDelivery({
        ...businessLockerDelivery,
        serviceId: response.orders.wardrobeDeliveryServiceId,
        freeDeliveryFrom: response.orders.wardrobeFreeDeliverFrom
      });
    }).catch((err) => console.log(err));
  }

  const openPage = (type: DeliveryType) => {
    switch (type) {
      case DeliveryType.SalesPoint:
        routeNavigator.goToSalesPointDelivery();
        break;
      case DeliveryType.Wardrobe:
        dispatch(updateDeliveryService(businessLockerDelivery.service));
        dispatch(updateFreeDeliveryFrom(businessLockerDelivery.freeDeliveryFrom));
        routeNavigator.goToBusinessLockerDelivery();
        break;
      case DeliveryType.Address:
        dispatch(updateDeliveryService(homeDelivery.service));
        dispatch(updateFreeDeliveryFrom(homeDelivery.freeDeliveryFrom));
        routeNavigator.goToAddressDelivery();
        break;
      case DeliveryType.ForeignLocker:
        dispatch(updateDeliveryService(parcelLockerDelivery.service));
        dispatch(updateFreeDeliveryFrom(parcelLockerDelivery.freeDeliveryFrom));
        routeNavigator.goToParcelLockerProviders();
        break;
    }
  }

  const itemImage = (type: DeliveryType) => {
    switch (type) {
      case DeliveryType.SalesPoint:
        return StoreIcon;
      case DeliveryType.Wardrobe:
        return BusinessLockerIcon;
      case DeliveryType.Address:
        return HomeIcon;
      case DeliveryType.ForeignLocker:
        return ClosetIcon;
    }
  }

  const isEnabled = (type: DeliveryType) => {
    if (type === DeliveryType.ForeignLocker) {
      if (!onlyPhysicalItemsInBasket())
        return false;

      return parcelLockerPriceUpToFreeDelivery <= 0 || parcelLockerDelivery.price > 0;
    }

    if (type === DeliveryType.Address) {
      return priceUpToFreeDelivery <= 0 || homeDelivery.price > 0;
    }

    if (type === DeliveryType.Wardrobe) {
      return businessLockerPriceUpToFreeDelivery <= 0 || businessLockerDelivery.price > 0;
    }

    return true;
  }

  const getDescription = (type: DeliveryType) => {
    switch (type) {
      case DeliveryType.Address:
        if (priceUpToFreeDelivery > 0) {
          return [
            t('payment:freeDeliveryFrom', { price: homeDelivery.freeDeliveryFrom.toPrice() }),
            t('payment:priceUpToFreeDelivery', { price: priceUpToFreeDelivery.toPrice() })
          ];
        }
        return;

      case DeliveryType.ForeignLocker:
        if (!onlyPhysicalItemsInBasket())
          return [t('payment:deliveryToParcelLockerForGoods')];

        if (parcelLockerPriceUpToFreeDelivery > 0) {
          return [
            t('payment:parcelLockerFreeDeliveryFrom', { price: parcelLockerDelivery.freeDeliveryFrom.toPrice() }),
            t('payment:priceUpToFreeDelivery', { price: parcelLockerPriceUpToFreeDelivery.toPrice() })
          ];
        }

        return;

      case DeliveryType.Wardrobe:
        if (businessLockerPriceUpToFreeDelivery > 0) {
          return [
            t('payment:businessLockerFreeDeliveryFrom', { price: businessLockerDelivery.freeDeliveryFrom.toPrice() }),
            t('payment:priceUpToFreeDelivery', { price: businessLockerPriceUpToFreeDelivery.toPrice() })
          ];
        }
        return;

      default:
        return [];
    }
  }

  return (
    <Grid container direction="column" alignItems="center">
      <NavBar barTitle={t('payment:whereToDeliver')} />
      <BreadcrumbsBar paths={[{ path: CartRoute, isLink: true }, { path: WhereToRoute }]} />
      <Grid
        sx={{ maxWidth: "1000px" }}
        container
        direction="column"
        justifyContent="center"
        alignItems="center">
        <Box sx={{ width: appStyleMode !== AppStyle.Mobile ? "90%" : null }} className={appStyleMode === AppStyle.Mobile && "where-to-list"}>
          {deliveryOptions?.map((option) => (
            <WhereToItem
              title={option.name}
              openPage={() => openPage(option.type)}
              disabled={!isEnabled(option.type)}
              description={getDescription(option.type)}
              icon={<ImageLoader src={itemImage(option.type)} sx={{ height: "100px" }} alt={option.name} />} />
          ))}
        </Box>
      </Grid>
    </Grid >
  );
}
